// This is a temporary implementation of Revieve VTO for evaluating their out-of-the-box solution

import type { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { Spacing8, Spacing12, Spacing16, Spacing24 } from 'autogen/design-tokens/tokens';
import type { VariantPickerListItem } from 'autogen/swagger/all';
import { VariantError, VariantPrefix } from 'autogen/translation-keys/trans-website-product-vto';
import ItemCarousel from 'components/generic/ScrollSnapSlider/components/ItemCarousel';
import { Typography } from 'components/generic/typography';
import SwatchRenderer from 'components/product/Variants/Swatches/SwatchRenderer';
import { invisibleScrollbar } from 'modules/helpers/mixins';

type VariantSeletorProps = {
    variants?: VariantPickerListItem[];
    selectedItem?: VariantPickerListItem;
    setSelectedItem: Dispatch<SetStateAction<VariantPickerListItem | undefined>>;
    focusedItem?: VariantPickerListItem;
    setFocusedItem: Dispatch<SetStateAction<VariantPickerListItem | undefined>>;
    setError: Dispatch<SetStateAction<string | undefined>>;
};

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${Spacing8};
    padding: ${Spacing16} 0;

`;

const SelectedVariant = styled.div`
    display: flex;
    gap: ${Spacing8};
    padding: 0 ${Spacing24};
`;

const scrollContainerStyling = css`
    && {
        ${invisibleScrollbar}
        padding-left: ${Spacing24};
        padding-right: ${Spacing24};
        scroll-padding-inline-start: ${Spacing24};
        scroll-padding-inline-end: ${Spacing24};
    }
`;

function VariantSelector(props: VariantSeletorProps) {
    const { variants, selectedItem, setSelectedItem, focusedItem, setFocusedItem, setError } = props;
    const { t } = useTranslation();

    const update = (productId: string) => {
        window.Revieve.API.liveAR.isTryOnAvailable({ product_id: productId }, (available) => {
            if (available) {
                setError(undefined);
                window.Revieve.API.liveAR.addTryOnProduct(productId);
            } else {
                setError(t(VariantError));
            }
        });
    };

    if (!variants) return;

    return (
        <Wrapper>
            <SelectedVariant>
                <Typography.Body size="M" as="div">
                    {t(VariantPrefix)}
                </Typography.Body>
                <Typography.Body size="M" strong as="div">
                    {focusedItem?.name}
                </Typography.Body>
            </SelectedVariant>
            <ItemCarousel
                scrollContainerCss={scrollContainerStyling}
                items={variants}
                itemRenderer={(variant, i) => {
                    variant.isSelected = selectedItem?.productId === variant.productId;

                    return (
                        <div key={variant.productId} data-slide-index={i}>
                            <SwatchRenderer
                                size={variant.displayType === 'Image' ? 'sm' : 'lg'}
                                {...variant}
                                onMouseOver={() => {
                                    setFocusedItem(variant);
                                }}
                                onMouseLeave={() => {
                                    setFocusedItem(selectedItem);
                                }}
                                onFocus={() => {
                                    setFocusedItem(variant);
                                }}
                                onBlur={() => {
                                    setFocusedItem(selectedItem);
                                }}
                                onClick={() => {
                                    setSelectedItem(variant);
                                    update(variant.productId.toString());
                                }}
                            />
                        </div>
                    );
                }}
                carouselButtonsIndent={Spacing12}
            />
        </Wrapper>
    );
}

export default VariantSelector;
